import { Boundaries } from "@planzone/core";
import { gantt, Identifiable, Resource } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceGantt } from "../../common/gantt";
import { handleRetrieveProjectGantt } from "./retrieveProjectGantt";
import { handleRetrieveZoneGantt } from "./retrieveZoneGantt";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { size, sort, page, zone, resources }: Boundaries["retrieveGantt"]["request"],
): State {
  const resourceUuids =
    resources
      ?.map((r) => r.uuid)
      .sort()
      .join(",") || "";

  const index = state.findIndex((req) => {
    const reqResourceUuids =
      req.resources
        ?.map((r) => r.uuid)
        .sort()
        .join(",") || "";
    return (
      req.size === size &&
      req.sort === sort &&
      ((typeof req.zone === "undefined" && typeof zone === "undefined") ||
        req.zone?.uuid === zone?.uuid) &&
      resourceUuids === reqResourceUuids
    );
  });

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          gantt,
          size,
          page,
          zone,
          sort,
          resources,
          request: setRequestStateToPending(),
          canLoadMore: true,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    gantt,
    size,
    page,
    sort,
    zone,
    resources,
  }: Boundaries["retrieveGantt"]["success"],
): State {
  const resourceUuids =
    resources
      ?.map((r) => r.uuid)
      .sort()
      .join(",") || "";

  const index = state.findIndex((req) => {
    const reqResourceUuids =
      req.resources
        ?.map((r) => r.uuid)
        .sort()
        .join(",") || "";
    return (
      req.size === size &&
      req.sort === sort &&
      ((typeof req.zone === "undefined" && typeof zone === "undefined") ||
        req.zone?.uuid === zone?.uuid) &&
      resourceUuids === reqResourceUuids
    );
  });

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          gantt:
            page === 0
              ? gantt
              : {
                  projects: [...state[index].gantt.projects, ...gantt.projects],
                  constraints: [],
                },
          page: page + 1,
          request: setRequestStateToSuccess(),
          canLoadMore: gantt.projects.length >= size,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          gantt,
          sort,
          size,
          page,
          zone,
          resources,
          request: setRequestStateToSuccess(),
          canLoadMore: gantt.projects.length >= size,
        },
      ];
}

function handleFailure(
  state: State,
  { size, sort, page, zone, resources }: Boundaries["retrieveGantt"]["failure"],
): State {
  const resourceUuids =
    resources
      ?.map((r) => r.uuid)
      .sort()
      .join(",") || "";

  const index = state.findIndex((req) => {
    const reqResourceUuids =
      req.resources
        ?.map((r) => r.uuid)
        .sort()
        .join(",") || "";
    return (
      req.size === size &&
      req.sort === sort &&
      ((typeof req.zone === "undefined" && typeof zone === "undefined") ||
        req.zone?.uuid === zone?.uuid) &&
      resourceUuids === reqResourceUuids
    );
  });

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          gantt,
          size,
          page,
          sort,
          zone,
          resources,
          request: setRequestStateToFailure(),
          canLoadMore: true,
        },
      ];
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    gantt: reduceGantt(req.gantt, event),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveGanttRequested]: handleRequest,
      [events.retrieveGanttSucceeded]: handleSuccess,
      [events.retrieveGanttFailed]: handleFailure,
      [events.retrieveProjectGanttSucceeded]: handleRetrieveProjectGantt,
      [events.retrieveZoneGanttSucceeded]: handleRetrieveZoneGantt,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
